import React from 'react';
import SEOHead from '../ui/SEOHead';
import PageHero from '../ui/PageHero';
import useScrollReveal from '../../hooks/useScrollReveal';
import { PRACTICE_INFO, OFFICE_HOURS } from '../../data/navigation';
import s from './Location.module.css';

const DIRECTIONS = [
  {
    from: 'From I-476 North (Blue Route)',
    icon: '🛣️',
    steps: [
      'Take Exit 13 toward Fort Washington / Flourtown',
      'Merge onto Germantown Pike heading west',
      'Turn right onto Bethlehem Pike (PA-309 N)',
      'Continue 0.8 miles — our office is on the right in the Fort Washington Marketplace',
    ],
  },
  {
    from: 'From PA-309 North',
    icon: '🧭',
    steps: [
      'Head north on Bethlehem Pike through Flourtown',
      'Pass the intersection of Bethlehem Pike & Stenton Ave',
      'Look for the Fort Washington Marketplace on the right',
      'We are in Suite B235 — second floor',
    ],
  },
  {
    from: 'From Center City Philadelphia',
    icon: '🏙️',
    steps: [
      'Take I-276 W (Pennsylvania Turnpike) or Lincoln Drive north',
      'Merge onto Germantown Ave (PA-152) heading north',
      'Turn left onto Bethlehem Pike (PA-309)',
      'Fort Washington Marketplace is 0.4 miles ahead on the left',
    ],
  },
];

const NEARBY = [
  { label: 'Parking', icon: '🅿️', text: 'Free parking lot directly in front of the building. Ample spaces available.' },
  { label: 'SEPTA Bus', icon: '🚌', text: 'Route 94 stops at Bethlehem Pike & Norristown Rd, a 3-minute walk.' },
  { label: 'Accessibility', icon: '♿', text: 'Elevator access to Suite B235. Wheelchair accessible throughout.' },
  { label: 'Nearby Landmarks', icon: '📍', text: 'Located in Fort Washington Marketplace, across from Giant supermarket.' },
];

export default function LocationPage() {
  useScrollReveal();

  return (
    <>
      <SEOHead
        title="Location & Directions | Celebrity Smile Studio – Flourtown, PA"
        description="Find Celebrity Smile Studio at 1811 Bethlehem Pike, Suite B235, Flourtown PA 19031. Get directions, office hours, and parking information."
        canonical="/contact/location"
      />

      <PageHero
        eyebrow="Find Us"
        title="Our <em>Location</em>"
        subtitle="Conveniently located in Flourtown, PA — serving Chestnut Hill, Fort Washington, Ambler, and surrounding communities."
        breadcrumbs={[{ label: 'Contact', path: '/contact' }, { label: 'Location' }]}
        image="https://images.unsplash.com/photo-1486325212027-8081e485255e?w=1600&q=80"
      />

      {/* ── Map + Info ── */}
      <section className="section" style={{ background: 'var(--blue-50)', paddingBottom: 0 }}>
        <div className="container">
          <div className={s.topGrid}>

            {/* Address + Hours card */}
            <div className={'reveal ' + s.infoPanel}>
              <span className="eyebrow">Address</span>
              <h2 className={'display-heading ' + s.addrHeading}>
                Celebrity<br/><em>Smile Studio</em>
              </h2>

              <div className={s.addrBlock}>
                <div className={s.addrIcon}>📍</div>
                <div>
                  <div className={s.addrLine}>1811 Bethlehem Pike, Suite B235</div>
                  <div className={s.addrLine}>Flourtown, PA 19031</div>
                  <a
                    href={PRACTICE_INFO.mapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={s.dirLink}
                  >
                    Get Directions →
                  </a>
                </div>
              </div>

              <div className={s.divider} />

              <h3 className={s.hoursTitle}>Office Hours</h3>
              <div className={s.hoursTable}>
                {OFFICE_HOURS.map((h, i) => (
                  <div key={i} className={s.hoursRow + (h.closed ? ' ' + s.closed : '')}>
                    <span className={s.hoursDay}>{h.day}</span>
                    <span className={s.hoursTime}>{h.hours}</span>
                  </div>
                ))}
              </div>

              <div className={s.divider} />

              <div className={s.phoneRow}>
                <div className={s.phoneIcon}>📞</div>
                <div>
                  <div className={s.phoneLabel}>Call Us</div>
                  <a href={'tel:' + PRACTICE_INFO.phoneRaw} className={s.phoneNum}>
                    {PRACTICE_INFO.phone}
                  </a>
                </div>
              </div>

              <a href="/book-appointment" className={'btn btn-primary ' + s.bookBtn}>
                📅 Book Appointment
              </a>
            </div>

            {/* Google Map embed */}
            <div className={'reveal reveal-delay-2 ' + s.mapWrap}>
              <div className={s.mapFrame}>
                <iframe
                  title="Celebrity Smile Studio Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3051.0!2d-75.2089!3d40.1066!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c6860b7af4e3f7%3A0xf13e7a30817c7fa6!2sCelebrity%20Smiles!5e0!3m2!1sen!2sus!4v1709500000000!5m2!1sen!2sus"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
              <div className={s.mapActions}>
                <a
                  href="https://www.google.com/maps/dir/?api=1&destination=Celebrity+Smiles+1811+Bethlehem+Pike+Flourtown+PA+19031"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={s.mapBtn}
                >
                  🗺️ Open in Google Maps
                </a>
                <a
                  href="https://maps.apple.com/?daddr=1811+Bethlehem+Pike+Flourtown+PA+19031"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={s.mapBtn}
                >
                  🍎 Open in Apple Maps
                </a>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── Parking / Transit / Access ── */}
      <section className="section" style={{ background: 'var(--blue-50)', paddingTop: '48px' }}>
        <div className="container">
          <div className={s.nearbyGrid}>
            {NEARBY.map((item, i) => (
              <div key={i} className={'reveal ' + s.nearbyCard} style={{ transitionDelay: `${i * 0.08}s` }}>
                <div className={s.nearbyIcon}>{item.icon}</div>
                <div className={s.nearbyLabel}>{item.label}</div>
                <p className={s.nearbyText}>{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Directions ── */}
      <section className="section">
        <div className="container">
          <div className={s.dirHeader + ' reveal'}>
            <span className="eyebrow">How to Find Us</span>
            <h2 className="display-heading">
              Directions to Our<br/><em>Office</em>
            </h2>
            <p className={s.dirIntro}>
              We're easy to reach from the Philadelphia suburbs. If you need help,
              call us at <a href={'tel:' + PRACTICE_INFO.phoneRaw} className={s.dirPhone}>{PRACTICE_INFO.phone}</a> and
              our team will guide you in.
            </p>
          </div>

          <div className={s.dirGrid}>
            {DIRECTIONS.map((dir, i) => (
              <div key={i} className={'reveal ' + s.dirCard} style={{ transitionDelay: `${i * 0.1}s` }}>
                <div className={s.dirCardTop}>
                  <span className={s.dirCardIcon}>{dir.icon}</span>
                  <h3 className={s.dirCardTitle}>{dir.from}</h3>
                </div>
                <ol className={s.dirSteps}>
                  {dir.steps.map((step, j) => (
                    <li key={j} className={s.dirStep}>{step}</li>
                  ))}
                </ol>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA Band ── */}
      <section className={s.ctaBand}>
        <div className="container">
          <div className={s.ctaInner + ' reveal'}>
            <div>
              <h2 className={s.ctaTitle}>Ready to Visit Us?</h2>
              <p className={s.ctaSub}>New patients are always welcome. Same-day emergency slots available.</p>
            </div>
            <div className={s.ctaActions}>
              <a href="/book-appointment" className="btn btn-primary">📅 Book Online</a>
              <a href={'tel:' + PRACTICE_INFO.phoneRaw} className="btn btn-outline loaction-btn">
                📞 {PRACTICE_INFO.phone}
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
